
import type { PropType } from 'vue'
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// validations
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

// composable
import useNotifications from '@/common/composable/useNotifications'

// types
import { SettingsWabaTemplatesUpdatePayload, SettingsWabaTemplatesLanguage, SettingsWabaTemplate, SettingsWabaTemplatesCategory } from '@/core/types/settings.types'

// realisations
import { settings } from '@/core/realisations/settings.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

export default defineComponent({
    components: { Modal, TheInput, TheTextarea, TheSelect, TheButton },
    props: {
        template: {
            type: Object as PropType<SettingsWabaTemplate>,
            default: null,
        }
    },
    emits: ['updated', 'cancel'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            //
            name: null as string | null,
            text: null as string | null,
            language: null as string | null,
            category: null as string | null,
        })

        const state_form$ = useVuelidate({
            name: { required },
            text: { required },
            language: { required },
            category: { required }
        }, state)

        const data_state = reactive({
            languages: new DataState<SettingsWabaTemplatesLanguage>({}),
            categories: new DataState<SettingsWabaTemplatesCategory>({})
        })

        const closeModal = () => {
            if (state.is_loading) return
            emit('cancel')
        }

        const updateTemplate = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                const payload: SettingsWabaTemplatesUpdatePayload = {
                    name: state.name as string,
                    text: state.text as string,
                    language: state.language as string,
                    category: state.category as string
                }

                await settings.updateWabaTemplate(props.template.id, payload)

                notifications.notify({ title: 'Шаблон успешно сохранен!', type: 'success' })
                emit('updated')
            } catch (error) {
                console.error(error)

                notifications.notifyError({
                    error,
                    title: 'При сохранении шаблона произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadLanguages = async () => {
            try {
                data_state.languages.is_loading = true

                const response = await settings.wabaTemplatesGetLanguages()
                data_state.languages.list = response.data

                data_state.languages.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.languages.is_failed = true
            } finally {
                data_state.languages.is_loading = false
            }
        }

        const loadCategories = async () => {
            try {
                data_state.categories.is_loading = true

                const response = await settings.getWabaTemplateCategories()
                data_state.categories.list = response.data

                data_state.categories.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.categories.is_failed = true
            } finally {
                data_state.categories.is_loading = false
            }
        }

        onBeforeMount(() => {
            if (props.template) {
                state.name = props.template.name
                state.text = props.template.text
                
                if (props.template.language) {
                    state.language = props.template.language.value
                }

                if (props.template.category) {
                    state.category = props.template.category.value
                }
            }

            loadLanguages()
            loadCategories()
        })

        return {
            state,
            state_form$,
            data_state,
            //
            updateTemplate,
            loadLanguages,
            loadCategories,
            closeModal,
        }
    },
})
